import { socialExport } from '@mi-front/api-clients';
import { Box } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

type Props = {
  stationId: string;
  isLoading: boolean;
  onRefreshStateChange?: (isRefreshing: boolean) => void;
};

/**
 * Refresh link component for YouTube playlists.
 * Displays a clickable link to refresh playlists from YouTube.
 * Shows default helper text when refresh link is not visible.
 */
export function YoutubePlaylistsRefreshLink({ stationId, isLoading, onRefreshStateChange }: Props) {
  const queryClient = useQueryClient();
  const { $t } = useIntl();
  const [refreshLinkVisible, setRefreshLinkVisible] = useState(true);

  const refreshMutation = socialExport.useRefreshPlaylistsYoutube({
    mutation: {
      onSuccess: () => {
        // Invalidate the playlists query to trigger a refetch
        queryClient.invalidateQueries({
          queryKey: socialExport.getYoutubePlaylistsQueryKey(stationId),
        });
      },
    },
  });

  // Notify parent about refresh state changes
  useEffect(() => {
    if (onRefreshStateChange) {
      onRefreshStateChange(refreshMutation.isPending);
    }
  }, [refreshMutation.isPending, onRefreshStateChange]);

  const handleRefresh = (e: React.MouseEvent) => {
    e.preventDefault();
    if (isLoading || !stationId) return;
    setRefreshLinkVisible(false);
    refreshMutation.mutate({ station: stationId });
  };

  return (
    <Box component="span">
      {refreshLinkVisible && !isLoading && stationId ? (
        <>
          {$t({
            id: 'socialExport.youtube.playlist.notFound',
            defaultMessage: 'Playlist no encontrada?',
          })}{' '}
          <Box
            component="span"
            onClick={handleRefresh}
            sx={{
              cursor: 'pointer',
              color: 'primary.main',
              textDecoration: 'underline',
              '&:hover': {
                textDecoration: 'none',
              },
            }}
          >
            {$t({
              id: 'socialExport.youtube.playlist.refresh',
              defaultMessage: 'Actualizar listado',
            })}
          </Box>
        </>
      ) : (
        <>
          {$t({
            id: 'socialExport.youtube.playlist.helperText',
            defaultMessage: 'Selecciona una o más playlists',
          })}
        </>
      )}
    </Box>
  );
}
